import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { resolveWhatsAppMediaUrl } from '../utils/mediaUrl';
import { ContactActionModal } from './ContactActionModal';
import { renderWhatsAppText } from '../../components/renderWhatsAppText';

const CARD_WIDTH = 330;
const CARD_GAP = 10;

function resolveCardMediaUrl(attachments, cardIndex) {
  const attachment = attachments?.[cardIndex];
  if (!attachment?.file) return null;
  return resolveWhatsAppMediaUrl(attachment.file);
}

function isVideoMime(mimeType) {
  return typeof mimeType === 'string' && mimeType.startsWith('video/');
}

function NavArrow({ direction, onClick, imageHeight }) {
  const isNext = direction === 'next';
  return (
    <button
      type="button"
      aria-label={isNext ? 'Next card' : 'Previous card'}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`absolute z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-[#54656F] shadow-[0_1px_4px_rgba(11,20,26,0.26)] hover:bg-[#F5F6F6] ${
        isNext ? 'right-1' : 'left-1'
      }`}
      style={{ top: imageHeight / 2 }}
    >
      <svg
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.2}
        viewBox="0 0 24 24"
      >
        {isNext ? (
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        ) : (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 19l-7-7 7-7"
          />
        )}
      </svg>
    </button>
  );
}

function CarouselCard({
  card,
  mediaUrl,
  mimeType,
  isLast,
  cardWidth,
  onMediaClick,
  onPhoneClick,
}) {
  const [mediaError, setMediaError] = useState(false);
  const isVideo = isVideoMime(mimeType);

  const handleButtonClick = (btn) => {
    if ((btn.type === 'URL' || btn.type === 'url') && btn.url) {
      window.open(btn.url, '_blank', 'noopener,noreferrer');
    } else if (
      (btn.type === 'PHONE_NUMBER' || btn.type === 'phone') &&
      btn.phone_number
    ) {
      onPhoneClick?.(btn);
    }
  };

  return (
    <div
      data-card
      className={`shrink-0 snap-start self-stretch bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden flex flex-col shadow-sm ${isLast ? '' : 'mr-2.5'}`}
      style={{ width: cardWidth }}
    >
      <div
        className="w-full bg-[#F0F2F5] flex items-center justify-center overflow-hidden rounded-t-2xl relative shrink-0"
        style={{ height: Math.round(cardWidth * (186 / 330)) }}
      >
        {mediaUrl && !mediaError ? (
          <button
            type="button"
            className="relative h-full w-full cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onMediaClick?.();
            }}
          >
            {isVideo ? (
              <video
                src={mediaUrl}
                muted
                playsInline
                preload="metadata"
                className="h-full w-full object-cover"
                onError={() => setMediaError(true)}
              />
            ) : (
              <img
                src={mediaUrl}
                alt=""
                className="h-full w-full object-cover hover:opacity-90 transition-opacity"
                onError={() => setMediaError(true)}
              />
            )}
            {isVideo && (
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-black/55">
                  <svg
                    className="ml-0.5 h-5 w-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </span>
              </span>
            )}
          </button>
        ) : (
          <div className="flex flex-col items-center justify-center gap-1.5 text-[#8696A0]">
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              viewBox="0 0 24 24"
            >
              <rect
                x="3"
                y="3"
                width="18"
                height="18"
                rx="3"
                strokeWidth={1.5}
              />
              <circle cx="8.5" cy="8.5" r="1.5" strokeWidth={1.5} />
              <path
                strokeWidth={1.5}
                strokeLinecap="round"
                d="m21 15-5-5-4 4-2-2-5 5"
              />
            </svg>
            <span className="text-[11px] font-medium uppercase tracking-wide">
              {isVideo ? 'Video' : 'Image'}
            </span>
          </div>
        )}
      </div>

      <div className="px-3 py-2.5 flex-1 min-h-0">
        <p className="text-[14px] leading-5 text-[#111B21] whitespace-pre-wrap [overflow-wrap:anywhere]">
          {card.body ? (
            renderWhatsAppText(card.body)
          ) : (
            <span className="text-[#8696A0] italic">Card text...</span>
          )}
        </p>
      </div>

      {(card.buttons || []).length > 0 && (
        <div className="border-t border-[#E5E5E5] divide-y divide-[#E5E5E5]">
          {card.buttons.map((btn, btnIdx) => (
            <button
              key={btnIdx}
              type="button"
              onClick={() => handleButtonClick(btn)}
              className="w-full min-h-9 h-auto py-2 px-2 flex items-center justify-center gap-1.5 text-[13px] font-semibold text-[#0088FF] hover:bg-[#F0F8FF] active:bg-[#E0F0FF] transition-colors select-none text-center"
            >
              {(btn.type === 'URL' || btn.type === 'url') && (
                <svg
                  className="w-3.5 h-3.5 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              )}
              {(btn.type === 'PHONE_NUMBER' || btn.type === 'phone') && (
                <svg
                  className="w-3.5 h-3.5 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 5a2 2 0 012-2h3l2 5-2.5 1.5a11 11 0 005 5L14 14l5 2v3a2 2 0 01-2 2h-1C8.61 21 3 15.39 3 8V5z"
                  />
                </svg>
              )}
              <span className="[overflow-wrap:anywhere]">
                {btn.text || 'Button'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function CarouselMessageBubble({
  message,
  isIncoming,
  showTime = true,
  onMediaClick,
}) {
  const { template, attachments, time } = message;
  const cards = useMemo(
    () => (Array.isArray(template?.cards) ? template.cards : []),
    [template?.cards],
  );
  const bodyText = template?.body || message.content || '';

  const scrollerRef = useRef(null);
  const activeIndexRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(CARD_WIDTH);
  const [canGoPrev, setCanGoPrev] = useState(false);
  const [canGoNext, setCanGoNext] = useState(false);
  const [contactAction, setContactAction] = useState(null);
  const imageHeight = Math.round(cardWidth * (186 / 330));

  const carouselMedia = useMemo(
    () =>
      cards
        .map((card, idx) => {
          const attachment = attachments?.[idx];
          const file = attachment?.file;
          if (!file) return null;
          return {
            ...message,
            id: `${message.id}-card-${idx}`,
            mediaUrl: resolveWhatsAppMediaUrl(file),
            mimeType: attachment.mime_type || 'image/jpeg',
            fileName: attachment.filename || `card-${idx + 1}`,
          };
        })
        .filter(Boolean),
    [attachments, cards, message],
  );

  const syncFromScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScroll = Math.max(el.scrollWidth - el.clientWidth, 0);
    const overflows = maxScroll > 1;
    setCanGoPrev(overflows && el.scrollLeft > 1);
    setCanGoNext(overflows && el.scrollLeft < maxScroll - 1);
    const width = el.querySelector('[data-card]')?.offsetWidth || cardWidth;
    const index = overflows
      ? Math.round(el.scrollLeft / (width + CARD_GAP))
      : 0;
    const next = Math.min(Math.max(index, 0), Math.max(cards.length - 1, 0));
    activeIndexRef.current = next;
    setActiveIndex(next);
  }, [cardWidth, cards.length]);

  const scrollToIndex = useCallback(
    (index) => {
      const next = Math.min(Math.max(index, 0), Math.max(cards.length - 1, 0));
      const el = scrollerRef.current;
      const width = el?.querySelector('[data-card]')?.offsetWidth || cardWidth;
      activeIndexRef.current = next;
      setActiveIndex(next);
      el?.scrollTo({
        left: next * (width + CARD_GAP),
        behavior: 'smooth',
      });
    },
    [cardWidth, cards.length],
  );

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const measure = () => {
      const available = el.clientWidth;
      if (available < 32) return;
      const nextWidth = Math.min(CARD_WIDTH, available);
      setCardWidth((prev) => (prev === nextWidth ? prev : nextWidth));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [cards.length]);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const left = activeIndexRef.current * (cardWidth + CARD_GAP);
    if (Math.abs(el.scrollLeft - left) > 1) {
      el.scrollLeft = left;
    }
    syncFromScroll();
  }, [cardWidth, cards.length, syncFromScroll]);

  const bubbleCls = isIncoming
    ? 'bg-[#F5F5F5] text-[#111B21] rounded-[16px] rounded-tl-[4px]'
    : 'bg-primary text-white rounded-[16px] rounded-tr-[4px]';

  const timeColor = isIncoming ? 'text-[#A3A3A3]' : 'text-white/70';

  return (
    <div
      className={`flex w-full items-end mb-4 ${
        isIncoming ? 'justify-start' : 'justify-end'
      }`}
    >
      <div
        className={`flex flex-col w-full max-w-full gap-2 min-w-0 overflow-hidden ${
          isIncoming ? 'items-start' : 'items-end'
        }`}
      >
        <div className={`w-fit px-4 py-2.5 ${bubbleCls}`}>
          <p className="text-[14px] leading-5 whitespace-pre-wrap [overflow-wrap:anywhere]">
            {renderWhatsAppText(bodyText)}
          </p>
          {showTime && (
            <div
              className={`flex items-center mt-1 ${
                isIncoming ? 'justify-start' : 'justify-end'
              }`}
            >
              <span className={`text-[11px] ${timeColor}`}>{time}</span>
            </div>
          )}
        </div>

        {cards.length > 0 && (
          <div className="flex flex-col gap-2 min-w-0 w-full">
            <div className="relative min-w-0">
              <div
                ref={scrollerRef}
                className={`overflow-x-auto overflow-y-hidden overscroll-x-contain no-scrollbar snap-x pb-1 [touch-action:pan-x] ${
                  cardWidth >= 300 ? 'snap-proximity' : 'snap-mandatory'
                }`}
                onScroll={syncFromScroll}
              >
                <div
                  className={`flex w-max items-stretch ${isIncoming ? '' : 'ml-auto'}`}
                >
                  {cards.map((card, index) => {
                    const mediaUrl = resolveCardMediaUrl(attachments, index);
                    const mimeType = attachments?.[index]?.mime_type;
                    return (
                      <CarouselCard
                        key={card.card_index ?? index}
                        card={card}
                        mediaUrl={mediaUrl}
                        mimeType={mimeType}
                        isLast={index === cards.length - 1}
                        cardWidth={cardWidth}
                        onMediaClick={() => {
                          if (!mediaUrl) return;
                          onMediaClick?.({
                            id: `${message.id}-card-${index}`,
                            mediaList: carouselMedia,
                          });
                        }}
                        onPhoneClick={(btn) => {
                          setContactAction({
                            phoneNumber: btn.phone_number,
                            title: btn.text || 'Contact',
                          });
                        }}
                      />
                    );
                  })}
                </div>
              </div>
              {canGoPrev && (
                <NavArrow
                  direction="prev"
                  imageHeight={imageHeight}
                  onClick={() => scrollToIndex(activeIndex - 1)}
                />
              )}
              {canGoNext && (
                <NavArrow
                  direction="next"
                  imageHeight={imageHeight}
                  onClick={() => scrollToIndex(activeIndex + 1)}
                />
              )}
            </div>

            {cards.length > 1 && (
              <div
                className={`flex items-center gap-1.5 ${
                  isIncoming ? 'justify-start pl-1' : 'justify-end pr-1'
                }`}
              >
                {cards.map((card, index) => (
                  <span
                    key={card.card_index ?? index}
                    className={`rounded-full transition-all duration-200 ${
                      index === activeIndex
                        ? 'w-4 h-1.5 bg-primary'
                        : 'w-1.5 h-1.5 bg-[#D1D7DB]'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <ContactActionModal
        action={contactAction}
        onClose={() => setContactAction(null)}
      />
    </div>
  );
}
